import React, { useState, useEffect } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import { supabase } from '../supabaseClient';
import { Calendar, Clock, User, CheckCircle, MessageSquare, MapPin, Info, AlertCircle, Check } from 'lucide-react';
import './PublicConfirm.css';

const PublicConfirm = () => {
  const { id } = useParams();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const actionParam = searchParams.get('action');
  const isReadOnly = actionParam === 'confirm';

  const [appointment, setAppointment] = useState(null);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState('idle'); // idle, confirming, success, error

  // Form states
  const [day, setDay] = useState('');
  const [date, setDate] = useState('');
  const [time, setTime] = useState('');
  const [preparation, setPreparation] = useState('');
  const [address, setAddress] = useState('');
  const [details, setDetails] = useState('');

  // Day of the week parser in Spanish
  const getDayName = (dateStr) => {
    if (!dateStr) return '';
    const daysOfWeek = ['Domingo', 'Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado'];
    const dateParts = dateStr.split('-');
    if (dateParts.length === 3) {
      // Create date using local parameters to avoid timezone offsets
      const dateObj = new Date(Number(dateParts[0]), Number(dateParts[1]) - 1, Number(dateParts[2]));
      return daysOfWeek[dateObj.getDay()];
    }
    return '';
  };

  useEffect(() => {
    const fetchApp = async () => {
      try {
        const { data, error } = await supabase.from('appointments').select('*').eq('id', id).single();
        if (error) throw error;
        setAppointment(data);
        
        // Pre-populate fields
        if (data) {
          setDay(getDayName(data.appointment_date));
          setDate(data.appointment_date || '');
          setTime(data.appointment_time?.substring(0, 5) || '');
          setAddress(data.study_details?.branch_name || 'Sede Central GDT');
          setPreparation(data.study_details?.fasting ? 'Ayuno de 8 horas. Llevar orden médica.' : '');
          setDetails(data.patient_message || '');
        }
      } catch (err) {
        console.error(err);
        setStatus('error');
      } finally {
        setLoading(false);
      }
    };
    if (id) fetchApp();
  }, [id]);

  const handleConfirm = async (e) => {
    e.preventDefault();
    setStatus('confirming');
    try {
      const updatedStudyDetails = {
        ...(appointment.study_details || {}),
        confirmed_day: day,
        confirmed_date: date,
        confirmed_time: time,
        confirmed_preparation: preparation,
        confirmed_address: address,
        confirmed_details: details
      };

      const { error } = await supabase
        .from('appointments')
        .update({ 
          verification_status: 'confirmed',
          status: 'confirmado',
          patient_message: details,
          study_details: updatedStudyDetails
        })
        .eq('id', id);
        
      if (error) throw error;

      // Create internal notification
      await supabase.from('notifications').insert([{
        title: 'Turno Confirmado por Paciente',
        message: `${appointment.patient_name} confirmó su asistencia para el ${date} a las ${time} hs.`,
        type: 'info',
        is_read: false
      }]);

      // Trigger email to clinic
      const htmlContent = `
        <div style="font-family: sans-serif; max-width: 600px; border: 1px solid #eee; padding: 20px; border-radius: 10px;">
          <h2 style="color: #00bfa5; border-bottom: 2px solid #00bfa5; padding-bottom: 10px; margin-top: 0;">✅ Turno Confirmado por el Paciente</h2>
          <p>El paciente <strong>${appointment.patient_name}</strong> ha confirmado su asistencia con los siguientes detalles:</p>
          
          <div style="background: #f8f9fa; padding: 15px; border-radius: 8px; margin: 15px 0; border: 1px solid #e0e0e0;">
            <h3 style="margin-top: 0; color: #333; font-size: 1.1rem; border-bottom: 1px solid #eee; padding-bottom: 5px;">Datos Confirmados</h3>
            <table style="width: 100%; border-collapse: collapse;">
              <tr>
                <td style="padding: 6px 0; color: #666; font-weight: bold; width: 120px;">Día:</td>
                <td style="padding: 6px 0; color: #333;">${day || 'No especificado'}</td>
              </tr>
              <tr>
                <td style="padding: 6px 0; color: #666; font-weight: bold;">Fecha:</td>
                <td style="padding: 6px 0; color: #333;">${date || 'No especificada'}</td>
              </tr>
              <tr>
                <td style="padding: 6px 0; color: #666; font-weight: bold;">Hora:</td>
                <td style="padding: 6px 0; color: #333;">${time || 'No especificada'} hs</td>
              </tr>
              <tr>
                <td style="padding: 6px 0; color: #666; font-weight: bold;">Dirección:</td>
                <td style="padding: 6px 0; color: #333;">${address || 'No especificada'}</td>
              </tr>
            </table>
          </div>

          <div style="background: #fff8e1; padding: 15px; border-radius: 8px; margin: 15px 0; border: 1px solid #ffe082;">
            <h4 style="margin-top: 0; color: #f57c00; font-size: 1rem; margin-bottom: 8px;">💊 Preparación:</h4>
            <p style="margin: 0; color: #555; font-size: 0.95rem;">${preparation || '<em>Ninguna o sin especificar</em>'}</p>
          </div>

          <div style="background: #e3f2fd; padding: 15px; border-radius: 8px; margin: 15px 0; border: 1px solid #bbdefb;">
            <h4 style="margin-top: 0; color: #1565c0; font-size: 1rem; margin-bottom: 8px;">📝 Detalles / Comentarios:</h4>
            <p style="margin: 0; color: #333; font-size: 0.95rem;">${details || '<em>Sin comentarios adicionales</em>'}</p>
          </div>
        </div>
      `;
      
      await supabase.functions.invoke('resend', {
        body: {
          to: 'info@gestionamientodeturnos.com',
          subject: `✅ Turno Confirmado: ${appointment.patient_name}`,
          html: htmlContent
        }
      });

      setStatus('success');
    } catch (err) {
      console.error(err);
      setStatus('error');
    }
  };

  if (loading) return <div className="public-confirm-container"><div className="loading-spinner"></div></div>;
  if (!appointment) return <div className="public-confirm-container"><h2>Turno no encontrado</h2></div>;
  
  if (status === 'success') return (
    <div className="public-confirm-container success-view">
      <div className="success-card glass-card-confirm">
        <div className="success-icon-wrapper">
          <CheckCircle size={80} color="#00e676" />
        </div>
        <h2>¡Turno Confirmado con Éxito!</h2>
        <p className="success-subtitle">Hemos recibido los datos de confirmación y actualizado su agenda médica. Lo esperamos.</p>
        
        <div className="confirmed-summary-box">
          <div className="summary-title">Resumen de Asistencia Confirmada</div>
          <div className="summary-grid">
            <div className="summary-item"><Calendar size={16} /> <span><strong>Fecha:</strong> {day}, {date}</span></div>
            <div className="summary-item"><Clock size={16} /> <span><strong>Hora:</strong> {time} hs</span></div>
            <div className="summary-item"><MapPin size={16} /> <span><strong>Dirección:</strong> {address}</span></div>
            {preparation && (
              <div className="summary-item full"><Info size={16} /> <span><strong>Prep:</strong> {preparation}</span></div>
            )}
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div className="public-confirm-container">
      <div className="confirm-card glass-card-confirm">
        <div className="confirm-card-header">
          <h2>Confirmación de Turno Médico</h2>
          <p className="subtitle">Por favor, verifique y complete la información para su asistencia.</p>
        </div>
        
        <div className="patient-banner">
          <div className="patient-avatar-box">
            <User size={20} />
          </div>
          <div className="patient-banner-info">
            <div className="p-name">{appointment.patient_name}</div>
            <div className="p-dni">Documento: {appointment.study_details?.patient_dni || 'No especificado'}</div>
            <div className="p-study">Estudio: {appointment.study_type || 'Consulta General'}</div>
          </div>
        </div>

        <form onSubmit={handleConfirm} className="confirm-main-form">
          <div className="form-section-title">
            <Info size={16} />
            <span>Verificar Día, Fecha y Hora del Turno</span>
          </div>

          <div className="confirm-form-grid">
            <div className="confirm-form-group">
              <label>Día de la Semana</label>
              <div className="input-with-icon">
                <Calendar size={16} className="input-icon" />
                <input 
                  type="text" 
                  value={day} 
                  onChange={(e) => setDay(e.target.value)} 
                  placeholder="Ej: Jueves"
                  required
                  readOnly={isReadOnly}
                  style={isReadOnly ? { backgroundColor: 'rgba(255,255,255,0.05)', color: '#888', cursor: 'not-allowed' } : {}}
                />
              </div>
            </div>

            <div className="confirm-form-group">
              <label>Fecha</label>
              <div className="input-with-icon">
                <Calendar size={16} className="input-icon" />
                <input 
                  type="date" 
                  value={date} 
                  onChange={(e) => setDate(e.target.value)} 
                  required
                  readOnly={isReadOnly}
                  style={isReadOnly ? { backgroundColor: 'rgba(255,255,255,0.05)', color: '#888', cursor: 'not-allowed' } : {}}
                />
              </div>
            </div>

            <div className="confirm-form-group">
              <label>Hora del Turno</label>
              <div className="input-with-icon">
                <Clock size={16} className="input-icon" />
                <input 
                  type="text" 
                  value={time} 
                  onChange={(e) => setTime(e.target.value)} 
                  placeholder="Ej: 16:15"
                  required
                  readOnly={isReadOnly}
                  style={isReadOnly ? { backgroundColor: 'rgba(255,255,255,0.05)', color: '#888', cursor: 'not-allowed' } : {}}
                />
              </div>
            </div>

            <div className="confirm-form-group">
              <label>Dirección del Centro / Consultorio</label>
              <div className="input-with-icon">
                <MapPin size={16} className="input-icon" />
                <input 
                  type="text" 
                  value={address} 
                  onChange={(e) => setAddress(e.target.value)} 
                  placeholder="Dirección del consultorio"
                  required
                  readOnly={isReadOnly}
                  style={isReadOnly ? { backgroundColor: 'rgba(255,255,255,0.05)', color: '#888', cursor: 'not-allowed' } : {}}
                />
              </div>
            </div>

            <div className="confirm-form-group full-width">
              <label>Indicaciones / Preparación previa al estudio</label>
              <div className="textarea-with-icon">
                <AlertCircle size={16} className="textarea-icon" />
                <textarea 
                  rows="2" 
                  value={preparation} 
                  onChange={(e) => setPreparation(e.target.value)} 
                  placeholder="Ej: Ayuno de 8 horas, traer estudios anteriores, etc..."
                  readOnly={isReadOnly}
                  style={isReadOnly ? { backgroundColor: 'rgba(255,255,255,0.05)', color: '#888', cursor: 'not-allowed' } : {}}
                />
              </div>
            </div>

            <div className="confirm-form-group full-width">
              <label>Detalles y Aclaraciones adicionales</label>
              <div className="textarea-with-icon">
                <MessageSquare size={16} className="textarea-icon" />
                <textarea 
                  rows="3" 
                  value={details} 
                  onChange={(e) => setDetails(e.target.value)} 
                  placeholder="Escriba aquí cualquier consulta, demora o aviso que requiera hacernos..."
                  readOnly={isReadOnly}
                  style={isReadOnly ? { backgroundColor: 'rgba(255,255,255,0.05)', color: '#888', cursor: 'not-allowed' } : {}}
                />
              </div>
            </div>
          </div>

          <button 
            type="submit"
            className="btn-confirm-action" 
            disabled={status === 'confirming'}
          >
            {status === 'confirming' ? (
              <span className="btn-loader-text">Procesando...</span>
            ) : (
              <>
                <Check size={18} />
                <span>{isReadOnly ? 'CONFIRMAR MI ASISTENCIA' : 'ENVIAR MODIFICACIONES'}</span>
              </>
            )}
          </button>
        </form>
      </div>
    </div>
  );
};

export default PublicConfirm;
